import { afterNextRender, Component, DOCUMENT, effect, inject, type OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { environment } from '../environments/environment';

import { LoadingScreenService } from './shared/components/loading-screen/loading-screen.service';
import { DynamicTitleService } from './shared/services/dynamic-title.service';
import { NgSwService } from './shared/services/ngsw.service';
import { SharedService } from './shared/services/shared.service';
import { TranslationService } from './shared/services/translation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private translationService = inject(TranslationService);
  loadingScreenService = inject(LoadingScreenService);
  private sharedService = inject(SharedService);
  private title = inject(Title);
  dynamicTitleService = inject(DynamicTitleService);
  private ngSwService = inject(NgSwService);
  private document = inject<Document>(DOCUMENT);

  private _routerLoaderToken?: string;

  constructor() {
    const savedLang = localStorage.getItem('lang') || 'en';
    this.translate.use(savedLang);

    effect(() => {
      const lang = this.translationService.languageChanged();
      if (lang) {
        this.translationService.reloadActiveRouteScope(this.route);
      }
    });

    afterNextRender(() => {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this._routerLoaderToken = this.loadingScreenService.show();
        } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          this.loadingScreenService.hide(this._routerLoaderToken);
          this._routerLoaderToken = undefined;
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.translationService.init();
    } catch (err) {
      console.error('Translation init failed, continuing app startup', err);
    }

    this.updateFavicon(environment.erpFaviconUrl);

    // ✅ Initialize NGSW management logic
    this.ngSwService.init().catch(err => console.error('Service Worker init failed:', err));

    // Set theme class dynamically based on client selection
    this.sharedService.updateClientTheme();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const childRoute = this.getDeepestRoute(this.route);

      const pageTitle =
        childRoute.snapshot.data['title'] ||
        (childRoute.snapshot.data['titleKey'] ? this.dynamicTitleService.getTitle(childRoute.snapshot.data['titleKey']) : '');

      this.title.setTitle(pageTitle);

      const currentRoute = childRoute.snapshot.root.firstChild;
      if (currentRoute) {
        currentRoute.data['title'] = pageTitle;
        this.sharedService.updatePageInfo(currentRoute.data);
      }
    });
  }

  getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private updateFavicon(iconUrl: string): void {
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel*='icon']");
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.type = 'image/png';
    link.href = iconUrl;
  }
}
